import React, { useEffect, useRef } from 'react';
import { Sparkles, Layers, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const services = [
  {
    icon: Sparkles,
    title: 'AI-Powered Products',
    metric: '10x',
    metricLabel: 'faster deployment',
    description: 'We built LoginRadius\u2019 AI Auth Studio — generating branded auth screens in under 30 seconds with 95%+ accuracy. From LLM integrations to computer vision, we ship production-ready AI.',
    features: ['Custom LLM & multi-model pipelines', 'Document AI & intelligent automation', 'Predictive analytics & real-time ML'],
    href: '/services/ai-solutions',
  },
  {
    icon: Layers,
    title: 'Full-Stack Platforms',
    metric: '$2B+',
    metricLabel: 'transactions processed',
    description: 'Our platforms process billions in transactions. InvestAssist handles $2B+ in CRE deals annually. We take products from concept to production within fixed-fee budgets.',
    features: ['React, Node.js, Python, AWS', 'Scalable data architecture & APIs', 'Fixed timelines, production-grade CI/CD'],
    href: '/services/product-development',
  },
  {
    icon: Users,
    title: 'Engineering Teams',
    metric: '48hr',
    metricLabel: 'team matching',
    description: 'Senior engineers who integrate like they\u2019ve been on your team from day one. LoginRadius and other leading companies rely on us for critical engineering capacity.',
    features: ['Senior-level, vetted engineers only', 'Seamless integration in 48 hours', 'Scale up or down as needed'],
    href: '/services/staff-augmentation',
  },
];

const ServicesShowcase = () => {
  const refs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    refs.current.forEach((ref) => { if (ref) observer.observe(ref); });
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative py-24 sm:py-32 overflow-hidden" style={{ background: '#F8FAFC' }}>
      {/* Subtle background glow */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(38,147,255,0.04) 0%, transparent 70%)' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          ref={(el) => (refs.current[0] = el)}
          className="reveal text-center mb-14 sm:mb-16"
        >
          <p className="text-sm font-semibold uppercase tracking-widest mb-3 font-heading" style={{ color: '#2693FF' }}>
            What We Build
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 font-heading mb-5">
            Engineering that delivers results
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Not just code — outcomes. Every engagement is backed by our full-ownership model
            and a track record of 100+ successful deliveries.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          {services.map((service, index) => (
            <Link
              key={service.title}
              to={service.href}
              ref={(el) => (refs.current[index + 1] = el)}
              className="reveal card-lift bg-white rounded-2xl p-7 sm:p-8 border border-slate-100 group cursor-pointer block relative"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Metric badge */}
              <div className="flex items-center justify-between mb-5">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ background: 'rgba(38,147,255,0.08)' }}
                >
                  <service.icon className="w-6 h-6" style={{ color: '#2693FF' }} />
                </div>
                <div className="text-right">
                  <div className="font-heading text-xl font-bold" style={{ color: '#2693FF' }}>
                    {service.metric}
                  </div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider">
                    {service.metricLabel}
                  </div>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-xl font-semibold text-slate-900 font-heading mb-3 group-hover:text-[#2693FF] transition-colors">
                {service.title}
              </h3>

              {/* Description — outcome-led */}
              <p className="text-slate-500 mb-5 leading-relaxed text-sm">
                {service.description}
              </p>

              {/* Features */}
              <ul className="space-y-2.5 mb-6">
                {service.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: '#2693FF' }} />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Link */}
              <div className="flex items-center gap-1.5 text-sm font-medium" style={{ color: '#2693FF' }}>
                Learn more
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesShowcase;
